import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  Checkbox,
  IconButton,
  CircularProgress,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  Column,
  ColumnDef,
  Row,
  RowData,
  TableOptions,
} from '@tanstack/react-table';

import { EmptyState } from '../empty-state';
import type { EmptyStateProps } from '../empty-state';
import { ChevronUpIcon, ChevronDownIcon, ChevronSelectorVerticalIcon } from '../icons';
import { Pagination } from '../pagination';

type TableProps<TData extends RowData> = Omit<
  TableOptions<TData>,
  | 'getCoreRowModel'
  | 'getSortedRowModel'
  | 'getPaginationRowModel'
  | 'getExpandedRowModel'
  | 'getGroupedRowModel'
> & {
  isLoading?: boolean;
  enablePagination?: boolean;
  enableRowActions?: boolean;
  renderRowActionMenuItems?: (row: Row<TData>) => ReactNode;
  enableRowExpand?: boolean;
  renderRowExpandComponent?: (row: Row<TData>) => ReactNode;
  renderGroupLabel?: (row: Row<TData>) => ReactNode;
  emptyStateProps?: EmptyStateProps;
  onRowClick?: (row: Row<TData>) => void;
};

function getCellStyles<TData>(column: Column<TData>): SxProps<Theme> {
  const isPinned = column.getIsPinned();
  const size = column.columnDef.size;

  return {
    // Let long unbreakable values (emails, IDs, phone numbers) wrap instead of
    // overflowing into the neighbouring cell. `anywhere` also shrinks min-content,
    // so a column can fit the container width.
    overflowWrap: 'anywhere',
    ...(size !== undefined && {
      width: size,
      minWidth: size,
      maxWidth: size,
    }),
    ...(isPinned && {
      position: 'sticky',
      right: isPinned === 'right' ? column.getAfter('right') : undefined,
      left: isPinned === 'left' ? column.getStart('left') : undefined,
      zIndex: 1,
    }),
  };
}

export function Table<TData extends RowData>({
  isLoading,
  enablePagination,
  enableRowActions,
  renderRowActionMenuItems,
  enableRowExpand,
  renderRowExpandComponent,
  renderGroupLabel,
  initialState,
  onRowClick,
  ...props
}: TableProps<TData>) {
  const [leafExpanded, setLeafExpanded] = useState<Record<string, boolean>>({});

  const renderRowActionMenuItemsRef = useRef(renderRowActionMenuItems);
  const renderRowExpandComponentRef = useRef(renderRowExpandComponent);
  const renderGroupLabelRef = useRef(renderGroupLabel);
  const leafExpandedRef = useRef(leafExpanded);
  const handleLeafExpandRef = useRef((rowId: string) => {
    setLeafExpanded((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  });

  renderRowActionMenuItemsRef.current = renderRowActionMenuItems;
  renderRowExpandComponentRef.current = renderRowExpandComponent;
  renderGroupLabelRef.current = renderGroupLabel;
  leafExpandedRef.current = leafExpanded;

  const columnsWithActions = useMemo(() => {
    if (!enableRowActions && !enableRowExpand) return props.columns;

    const actionsColumn: ColumnDef<TData> = {
      id: '_actions',
      cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {renderRowActionMenuItemsRef.current?.(row)}
          {enableRowExpand && (
            <IconButton size="small" onClick={() => handleLeafExpandRef.current(row.id)}>
              {leafExpandedRef.current[row.id] ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </IconButton>
          )}
        </Box>
      ),
    };

    return [...props.columns, actionsColumn];
  }, [enableRowActions, enableRowExpand, props.columns]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getRowCanExpand: (row) => row.getIsGrouped(),
    autoResetExpanded: false,
    defaultColumn: { enableSorting: false },
    manualSorting: true,
    manualPagination: true,
    initialState: {
      columnPinning: { right: ['_actions'] },
      expanded: true,
      ...initialState,
    },
    ...props,
    enableRowSelection:
      props.enableRowSelection === true
        ? (row) => !row.getIsGrouped()
        : props.enableRowSelection,
    columns: columnsWithActions,
  });

  // офф рішення немає, але це корсино для всіх кейсів тому тут така логіка присутня
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  useEffect(() => {
    if (!enablePagination || isLoading || pageIndex === 0 || pageIndex < pageCount)
      return;

    table.setPageIndex(Math.max(pageCount - 1, 0));
  }, [enablePagination, isLoading, pageIndex, pageCount, table]);

  return (
    <>
      <TableContainer>
        <MuiTable>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {props.enableRowSelection === true && (
                  <TableCell padding="checkbox" align="center">
                    <Checkbox
                      size="small"
                      checked={table.getIsAllPageRowsSelected()}
                      indeterminate={
                        table.getIsSomePageRowsSelected() &&
                        !table.getIsAllPageRowsSelected()
                      }
                      onChange={table.getToggleAllPageRowsSelectedHandler()}
                    />
                  </TableCell>
                )}
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id} sx={getCellStyles(header.column)}>
                    {header.column.getCanSort() ? (
                      <TableSortLabel
                        active={!!header.column.getIsSorted()}
                        direction={header.column.getIsSorted() || 'asc'}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' ? (
                          <ChevronUpIcon />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <ChevronDownIcon />
                        ) : (
                          <ChevronSelectorVerticalIcon />
                        )}
                      </TableSortLabel>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columnsWithActions.length +
                    (props.enableRowSelection === true ? 1 : 0)
                  }
                  sx={{ p: 0, borderBottom: 'none' }}
                >
                  <Box display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columnsWithActions.length +
                    (props.enableRowSelection === true ? 1 : 0)
                  }
                  sx={{ textAlign: 'center', py: 6, border: 'none' }}
                >
                  {props.emptyStateProps && <EmptyState {...props.emptyStateProps} />}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                if (row.getIsGrouped()) {
                  return (
                    <TableRow
                      key={row.id}
                      sx={(theme) => ({
                        '& .MuiTableCell-root': {
                          backgroundColor: theme.palette.gray[50],
                          ...theme.applyStyles('dark', {
                            backgroundColor: theme.palette.black[850],
                          }),
                        },
                      })}
                    >
                      {props.enableRowSelection === true && (
                        <TableCell padding="checkbox" align="center">
                          <Checkbox
                            size="small"
                            checked={row.getIsAllSubRowsSelected()}
                            indeterminate={
                              !row.getIsAllSubRowsSelected() &&
                              row.getLeafRows().some((leafRow) => leafRow.getIsSelected())
                            }
                            onChange={(event) => row.toggleSelected(event.target.checked)}
                          />
                        </TableCell>
                      )}
                      <TableCell colSpan={columnsWithActions.length}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {renderGroupLabelRef.current?.(row)}
                          <IconButton
                            size="small"
                            onClick={row.getToggleExpandedHandler()}
                          >
                            {row.getIsExpanded() ? (
                              <ChevronDownIcon />
                            ) : (
                              <ChevronUpIcon />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <Fragment key={row.id}>
                    <TableRow>
                      {props.enableRowSelection === true && (
                        <TableCell padding="checkbox" align="center">
                          <Checkbox
                            size="small"
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            onChange={row.getToggleSelectedHandler()}
                          />
                        </TableCell>
                      )}
                      {row.getVisibleCells().map((cell) => {
                        const isActionsCell = cell.column.id === '_actions';
                        const canClick = onRowClick && !isActionsCell;
                        return (
                          <TableCell
                            key={cell.id}
                            sx={{
                              ...getCellStyles(cell.column),
                              cursor: canClick ? 'pointer' : undefined,
                            }}
                            onClick={canClick ? () => onRowClick(row) : undefined}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {enableRowExpand && leafExpandedRef.current[row.id] && (
                      <TableRow>
                        <TableCell
                          colSpan={
                            row.getVisibleCells().length +
                            (props.enableRowSelection === true ? 1 : 0)
                          }
                          sx={{ p: 0 }}
                        >
                          {renderRowExpandComponentRef.current?.(row)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {enablePagination && (
        <Pagination
          count={Math.ceil(table.getRowCount() / table.getState().pagination.pageSize)}
          page={table.getState().pagination.pageIndex + 1}
          onPageChange={(page) => table.setPageIndex(page - 1)}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
        />
      )}
    </>
  );
}
